import { useEffect, useRef, useSyncExternalStore } from "react";
import {
  areBrandingFeatureFlagsReady,
  getActiveBrandTemplateId,
  getFeatureFlagsInitStatus,
  refreshFeatureFlagsForUi,
  subscribeFeatureFlags,
} from "../services/featureFlagService";
import { explainBrandResolution, resolveActiveBrand } from "../utils/auth";
import { getBrand, isBrandId, type BrandConfig } from "../services/ai/brandRegistry";
import { logBrandingFlagDiag } from "../../shared/featureFlags/brandingFlagDiag";

/**
 * React-bound view over the active company brand for the current user.
 *
 * Resolves at most one brand (auth CompanyId precedence, then the single
 * `pptxTemplateBrand` ConfigCat flag) via `resolveActiveBrand`, and re-renders the calling
 * component when any branding flag flips mid-session (config change, lazy
 * refresh, or client disposed on logout) through `useSyncExternalStore` +
 * `subscribeFeatureFlags`.
 *
 * Returns the resolved `BrandConfig` (a stable registry singleton) or
 * `null` when no brand toggle should be shown.
 */
export function useActiveBrand(companyId?: string | null): BrandConfig | null {
  const getSnapshot = (): string => {
    const status = getFeatureFlagsInitStatus();
    if (!areBrandingFeatureFlagsReady()) {
      return `${status}:`;
    }
    const brandId =
      resolveActiveBrand({ companyId, activeBrandTemplateId: getActiveBrandTemplateId() })?.id ?? "";
    return `${status}:${brandId}`;
  };
  const getServerSnapshot = (): string => "idle:";
  const snapshot = useSyncExternalStore(subscribeFeatureFlags, getSnapshot, getServerSnapshot);
  const [initStatus, activeBrandId] = snapshot.split(":");
  const activeBrand = getBrand(isBrandId(activeBrandId) ? activeBrandId : null) ?? null;

  useEffect(() => {
    const refresh = (reason: string): void => {
      refreshFeatureFlagsForUi(reason).catch((error) => {
        logBrandingFlagDiag("brandUiRefreshFailed", {
          reason,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    };

    refresh("activeBrandHookMounted");

    const onFocus = (): void => refresh("windowFocus");
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        refresh("documentVisible");
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const prevSnapshotRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevSnapshotRef.current === snapshot) return;
    prevSnapshotRef.current = snapshot;

    if (initStatus !== "ready") {
      logBrandingFlagDiag("brandResolutionSkipped", {
        companyId: companyId?.toString().trim() || null,
        initStatus,
        reason: "brandingFlagsNotReady",
      });
      return;
    }

    explainBrandResolution({ companyId, activeBrandTemplateId: getActiveBrandTemplateId() });
  }, [activeBrand?.id, companyId, initStatus, snapshot]);

  return activeBrand;
}
